import { getterTree, mutationTree, actionTree } from 'typed-vuex'
import { getKeys } from '~/lib/utils/utils'
import {
  FILTER_AMOUNT,
  FILTER_APPLICANT_COUNT, FILTER_CATEGORIES, FILTER_CONF_SCORE,
  FILTER_DEADLINE, FILTER_PAYMENT,
  FILTER_REQ_TYPE,
  FILTER_SEARCH,
  filterMethods
} from '~/lib/utils/filters'
import { Store } from '~/types/store'
import { ALL_TAB, IGNORED_TAB, PICKED_TAB } from '~/store/sideBar'

const areObjectsSame = <Type>(a:Type, b:Type):boolean => JSON.stringify(a).includes(JSON.stringify(b))

export const applyFilters = (appliedFilters: Store.Filter.FilterSet, list: Scholarship.General) => {
  // @ts-ignore
  return getKeys(appliedFilters).reduce((list, filterName) => {
    // @ts-ignore
    return filterMethods[filterName].apply(appliedFilters[filterName], list)
  }, list)
}

const searchFiler = () => ({ searchTerm: '' })
const rangeFilter = () => ({ min: 0, max: 0 })
const setFilter = () => ({ selected: [], required: [] })
const valueFilter = () => ({ selected: undefined })

export const filtarableTabs = [
  ALL_TAB,
  PICKED_TAB,
  IGNORED_TAB
]

export const filterSpecs = {
  [FILTER_SEARCH]: searchFiler,
  [FILTER_APPLICANT_COUNT]: rangeFilter,
  [FILTER_DEADLINE]: valueFilter,
  [FILTER_AMOUNT]: rangeFilter,
  [FILTER_REQ_TYPE]: setFilter,
  [FILTER_CONF_SCORE]: setFilter,
  [FILTER_CATEGORIES]: setFilter,
  [FILTER_PAYMENT]: setFilter
}

export const selectedFilters = (appliedFilters: Store.Filter.FilterSet) => {
  return getKeys(appliedFilters).reduce((acc, name) => {
    if (!areObjectsSame(appliedFilters[name], filterSpecs[name]())) {
      acc[name] = appliedFilters[name]
    }
    return acc
  }, {} as { [key in Store.Filter.Filter]: Store.Filter.FilterRule })
}

export const state = () => {
  return filtarableTabs.reduce((acc, tabName) => {
    acc[tabName] = getKeys(filterSpecs).reduce((acc, name) => {
      acc[name] = filterSpecs[name]()
      return acc
    }, {})
    return acc
  }, {})
}

export const getters = getterTree(state, {
  appliedFilterNames(state, getters, rootState) {
    const tabName = rootState.route.params.tabName
    let result = []
    if (!filtarableTabs.includes(tabName)) { return result }

    result = getKeys(filterSpecs)
      .filter(filterName => !areObjectsSame(state[tabName][filterName], filterSpecs[filterName]()))

    return result
  },
  appliedFilters(state, { appliedFilterNames, getFilter }, rootState) {
    const tabName = rootState.route.params.tabName
    return appliedFilterNames.reduce((acc, name) => {
      acc[name] = getFilter({ tabName, filterName: name })
      return acc
    }, {} as { [key in Store.Filter.Filter]: Store.Filter.FilterRule })
  },
  areFiltersApplied(state, getters) {
    return !!getters.appliedFilterNames.length
  },
  getFilter(state) {
    return ({ tabName, filterName }: { tabName: Store.TabName, filterName: Store.Filter.Filter }): Store.Filter.FilterRule => {
      if (!filtarableTabs.includes(tabName)) {
        return filterSpecs[filterName]()
      }

      return state[tabName][filterName]
    }
  },
  filteredList(state, getters) {
    return (tabName, list) => {
      // @ts-ignore
      list = getters.appliedFilterNames.reduce((list, filterName) => {
        // @ts-ignore
        return filterMethods[filterName].apply(getters.getFilter({ tabName, filterName }), list)
      }, list as Scholarship.General[])

      return list
    }
  }
})

const throughValues = (obj, cb) => {
  for (const prop of getKeys(obj)) {
    cb(prop, obj[prop])
  }
}

export const mutations = mutationTree(state, {
  addFilter(state, { tabName, filterName, value }: { tabName: Store.TabName, filterName: Store.Filter.Filter, value: Store.Filter.FilterRule }) {
    throughValues(value, (prop, value) => {
      state[tabName][filterName][prop] = value
    })
  },
  resetFilter(state, { tabName, filterName }: { tabName: Store.TabName, filterName: Store.Filter.Filter }) {
    if (!filtarableTabs.includes(tabName)) { return }
    throughValues(filterSpecs[filterName](), (prop, value) => {
      state[tabName][filterName][prop] = value
    })
  },
  resetAllFilters(state, { tabName }: { tabName: Store.TabName }) {
    if (!filtarableTabs.includes(tabName)) { return }
    for (const filterName of getKeys(filterSpecs)) {
      throughValues(filterSpecs[filterName](), (prop, value) => {
        state[tabName][filterName][prop] = value
      })
    }
  }
})

export const actions = actionTree(
  { state, getters, mutations },
  {
    applyFilter({ commit, getters }, { tabName, filterName, value }: { tabName: Store.FilterTab, filterName: Store.Filter.Filter, value: Store.Filter.FilterRule }) {
      if (areObjectsSame(getters.getFilter({ tabName, filterName }), value)) { return }
      commit('addFilter', { tabName, filterName, value })
    },
    resetFilter({ commit }, { tabName, filterName }) {
      commit('resetFilter', { tabName, filterName })
    },
    resetAllFilters({ commit }, { tabName }) {
      commit('resetAllFilters', { tabName })
    }
  }
)
